import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, Warning, Eye, CheckCircle, XCircle, Clock, Lightning, Flame,
  Plus, CircleNotch, UserCircle, CaretRight
} from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV_CONFIG = {
  warning: { color: "#FFCC00", label: "WARNING" },
  serious: { color: "#FF5A00", label: "SERIOUS" },
  critical: { color: "#FF0000", label: "CRITICAL" },
};

const STATUS_CONFIG = {
  pending_review: { color: "#FFCC00", label: "PENDING REVIEW", icon: Clock },
  approved: { color: "#34C759", label: "APPROVED FOR WORKER", icon: CheckCircle },
  dismissed: { color: "#404040", label: "DISMISSED", icon: XCircle },
  acknowledged: { color: "#002FA7", label: "WORKER ACKNOWLEDGED", icon: Eye },
};

function ViolationCard({ v, isAdmin, onUpdate }) {
  const sev = SEV_CONFIG[v.severity] || SEV_CONFIG.warning;
  const stat = STATUS_CONFIG[v.status] || STATUS_CONFIG.pending_review;
  const StatIcon = stat.icon;
  return (
    <div data-testid={`violation-${v.id}`} className="wp-card animate-slide-up border-l-2" style={{ borderLeftColor: sev.color }}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="font-mono-data text-[10px] px-1 py-0.5 rounded-sm" style={{ color: sev.color, background: `${sev.color}15`, border: `1px solid ${sev.color}` }}>{sev.label}</span>
            <span className="font-mono-data text-[10px] text-[#404040]">{v.osha_code}</span>
          </div>
          <h3 className="text-sm font-bold" style={{ fontFamily: "'Chivo', sans-serif" }}>{v.violation_type}</h3>
          <p className="text-[10px] text-[#404040] flex items-center gap-1 mt-0.5">
            <UserCircle size={10} /> {v.worker_name} | {new Date(v.created_at).toLocaleDateString()}
          </p>
        </div>
        <span className="flex items-center gap-0.5 font-mono-data text-[10px]" style={{ color: stat.color }}>
          <StatIcon size={10} /> {stat.label}
        </span>
      </div>
      <p className="text-xs text-[#404040] mb-2">{v.description}</p>
      {v.admin_notes && <p className="text-xs text-[#002FA7] bg-[#002FA708] p-2 rounded-sm mb-2"><b>Admin:</b> {v.admin_notes}</p>}
      {isAdmin && v.status === "pending_review" && (
        <div className="flex gap-2">
          <button onClick={() => onUpdate(v.id, { status: "approved", admin_notes: "Approved for worker review" })}
            className="flex-1 py-1.5 text-[10px] font-bold bg-[#34C759] text-white rounded-sm">Approve</button>
          <button onClick={() => onUpdate(v.id, { status: "dismissed", admin_notes: "Dismissed" })}
            className="flex-1 py-1.5 text-[10px] font-bold border border-[#E5E5E5] rounded-sm">Dismiss</button>
        </div>
      )}
      {!isAdmin && v.status === "approved" && !v.worker_acknowledged && (
        <button onClick={() => onUpdate(v.id, { worker_acknowledged: true, status: "acknowledged" })}
          className="w-full py-1.5 text-[10px] font-bold bg-[#002FA7] text-white rounded-sm">I Acknowledge This Violation</button>
      )}
    </div>
  );
}

function OshaRuleRow({ rule }) {
  return (
    <div className="flex items-start gap-2 p-2.5 border-b border-[#E5E5E5] last:border-0">
      <ShieldCheck size={14} className="text-[#002FA7] shrink-0 mt-0.5" />
      <div>
        <div className="flex items-center gap-1.5">
          <span className="font-mono-data text-[10px] font-bold text-[#0A0A0A]">{rule.code}</span>
          <span className="text-xs font-medium">{rule.title}</span>
        </div>
        <p className="text-[11px] text-[#404040] mt-0.5">{rule.description}</p>
      </div>
    </div>
  );
}

export default function SafetyPage() {
  const [rules, setRules] = useState(null);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [isAdmin] = useState(true);
  const [form, setForm] = useState({ worker_name: "", violation_type: "", osha_code: "", description: "", severity: "warning", camera_source: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesRes, violRes] = await Promise.all([
        axios.get(`${API}/osha/rules`),
        axios.get(`${API}/safety/violations`),
      ]);
      setRules(rulesRes.data);
      setViolations(violRes.data);
    } catch { toast.error("Failed to load safety data"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createViolation = async (e) => {
    e.preventDefault();
    if (!form.worker_name || !form.violation_type) { toast.error("Worker and violation type required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/safety/violations`, form);
      toast.success("Violation logged");
      setCreateOpen(false);
      setForm({ worker_name: "", violation_type: "", osha_code: "", description: "", severity: "warning", camera_source: "" });
      fetchData();
    } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const updateViolation = async (id, data) => {
    try {
      await axios.put(`${API}/safety/violations/${id}`, data);
      toast.success("Updated");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;

  const pending = violations.filter(v => v.status === "pending_review");
  const approved = violations.filter(v => v.status === "approved" || v.status === "acknowledged");

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="safety-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Chivo', sans-serif" }}>OSHA Safety</h1>
          <p className="text-[10px] font-mono-data text-[#404040]">RULES & VIOLATION TRACKING</p>
        </div>
        {isAdmin && (
          <button data-testid="log-violation-btn" onClick={() => setCreateOpen(true)}
            className="px-3 py-1.5 text-[10px] font-bold bg-[#FF5A00] text-white rounded-sm hover:bg-[#CC4800]">
            + Log Violation
          </button>
        )}
      </div>

      {pending.length > 0 && isAdmin && (
        <div className="wp-card mb-4 bg-[#FFCC0010] border-[#FFCC00]" data-testid="pending-violations">
          <span className="font-mono-data text-[10px] text-[#FFCC00]">PENDING REVIEW ({pending.length})</span>
        </div>
      )}

      <Tabs defaultValue="violations">
        <TabsList className="grid w-full grid-cols-2 h-9 mb-4">
          <TabsTrigger value="violations" className="text-xs" data-testid="safety-violations-tab">
            Violations {violations.length > 0 && `(${violations.length})`}
          </TabsTrigger>
          <TabsTrigger value="rules" className="text-xs" data-testid="safety-rules-tab">OSHA Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="violations">
          {violations.length === 0 ? (
            <div className="wp-card text-center py-8 text-sm text-[#34C759]"><CheckCircle size={24} className="mx-auto mb-2" />No violations on record.</div>
          ) : (
            <div className="space-y-3">
              {violations.map((v, i) => <div key={v.id} style={{ animationDelay: `${i * 0.05}s` }}><ViolationCard v={v} isAdmin={isAdmin} onUpdate={updateViolation} /></div>)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rules">
          {rules && (
            <div className="space-y-3">
              {[{key:"electrical",label:"ELECTRICAL",icon:Lightning},{key:"hvac",label:"HVAC",icon:Flame},{key:"general",label:"GENERAL",icon:ShieldCheck}].map(({key,label,icon:Icon}) => (
                <div key={key}>
                  <h3 className="font-mono-data text-[10px] mb-1 flex items-center gap-1" style={{color: key==="electrical"?"#002FA7":key==="hvac"?"#FF5A00":"#404040"}}><Icon size={10} />{label}</h3>
                  <div className="wp-card p-0">{rules[key]?.map((r,i)=><OshaRuleRow key={i} rule={r} />)}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Chivo', sans-serif" }}>Log Safety Violation</DialogTitle></DialogHeader>
          <form onSubmit={createViolation} className="space-y-3">
            <input data-testid="violation-worker-input" type="text" placeholder="Worker name" value={form.worker_name} onChange={e => setForm({...form, worker_name: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <input data-testid="violation-type-input" type="text" placeholder="Violation type (e.g., No PPE)" value={form.violation_type} onChange={e => setForm({...form, violation_type: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <input type="text" placeholder="OSHA Code (optional)" value={form.osha_code} onChange={e => setForm({...form, osha_code: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="serious">Serious</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7] resize-none h-16" />
            <input type="text" placeholder="Camera source (e.g., Body Cam #1)" value={form.camera_source} onChange={e => setForm({...form, camera_source: e.target.value})}
              className="w-full px-3 py-2 text-sm border border-[#E5E5E5] rounded-sm focus:outline-none focus:border-[#002FA7]" />
            <button data-testid="submit-violation-btn" type="submit" disabled={saving}
              className="w-full py-2.5 bg-[#FF5A00] text-white text-sm font-bold rounded-sm disabled:opacity-50">
              {saving ? "Logging..." : "Log Violation"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
